import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { linkify } from "@/components/ExpandableText";
import { toast } from "sonner";
import { Users2, Settings, Heart, Video, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { PAGE_TYPE_META } from "@/pages/Pages";
import PagePostCard from "@/components/PagePostCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PageDetail() {
  const { pageId } = useParams();
  const { user } = useAuth();
  const [page, setPage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, postsRes] = await Promise.all([
        axios.get(`${API}/pages/${pageId}`),
        axios.get(`${API}/pages/${pageId}/posts`),
      ]);
      setPage(pRes.data);
      setPosts(postsRes.data || []);
    } catch { toast.error("Página não encontrada"); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pageId]);
  const toggleFollow = async () => {
    try {
      const r = await axios.post(`${API}/pages/${pageId}/follow`);
      setPage({ ...page, is_following: r.data.following, followers_count: r.data.followers_count });
    } catch { toast.error("Erro"); }
  };

  const removePost = async (postId) => {
    if (!window.confirm("Excluir post?")) return;
    try {
      await axios.delete(`${API}/pages/${pageId}/posts/${postId}`);
      setPosts((prev) => prev.filter((x) => x.post_id !== postId));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  if (loading) return <div className="max-w-4xl mx-auto p-6"><Card className="p-10 text-center text-muted-foreground">Carregando...</Card></div>;
  if (!page) return null;

  const meta = PAGE_TYPE_META[page.type] || { label: page.type, icon: Users2, color: "text-muted-foreground bg-muted" };
  const Icon = meta.icon;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid={`page-detail-${page.page_id}`}>
      <Link to="/pages" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <Card className="overflow-hidden">
        <div
          className="h-40 md:h-56 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent relative"
          style={page.cover_url ? { backgroundImage: `url(${page.cover_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        >
          <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full ${meta.color} text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5`}>
            <Icon className="w-3.5 h-3.5" /> {meta.label}
          </div>
        </div>
        <div className="p-5 flex flex-wrap items-start gap-4 -mt-10">
          <div className="w-20 h-20 rounded-2xl border-4 border-card bg-card overflow-hidden flex-shrink-0">
            {page.avatar_url ? (
              <img src={page.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted"><Icon className="w-8 h-8 text-muted-foreground" /></div>
            )}
          </div>
          <div className="flex-1 min-w-0 pt-10">
            <h1 className="text-2xl font-bold tracking-tight" data-testid="page-detail-name">{page.name}</h1>
            <div className="text-sm text-muted-foreground">{page.category}</div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <Users2 className="w-3 h-3" /> {page.followers_count} seguidores · por {page.owner_name}
            </div>
            {page.description && (
              <p className="text-sm mt-2 whitespace-pre-wrap break-words">{linkify(page.description)}</p>
            )}
          </div>
          <div className="flex gap-2 pt-10">
            {page.is_owner && (
              <Link to={`/pages/${page.page_id}/manage`} data-testid="page-manage-link">
                <Button variant="outline" className="rounded-full gap-1"><Settings className="w-4 h-4" /> Gerenciar</Button>
              </Link>
            )}
            {user && !page.is_owner && (
              <Button
                onClick={toggleFollow}
                data-testid="page-follow-btn"
                className="rounded-full"
                variant={page.is_following ? "outline" : "default"}
              >
                {page.is_following ? "Deixar de seguir" : "Seguir"}
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Publicações</h2>
        {posts.length === 0 && (
          <Card className="p-10 text-center text-muted-foreground" data-testid="page-posts-empty">
            Ainda não há publicações desta página.
          </Card>
        )}
        {posts.map((p) => (
          <PagePostCard key={p.post_id} page={page} post={p} onDelete={removePost} />
        ))}
      </div>
    </div>
  );
}
